import { useRef, useState, type ReactNode } from 'react';

import { HotkeyLabel } from '@/components/blocks/Hotkey';
import { useSearchFocusState } from '@/hooks/useSearchFocusState';
import { cn } from '@/lib/utils';
import { classList } from '@/utils/cn';

import { XStack } from '../layout';
import { ScrollRestoration } from '../ScrollRestoration';

import { BaseEditorProvider, useEditorContainer } from './base-editor-context';
import { EditorContent, EditorRoot } from './components/editor';
import { EditorAskSelectionButton } from './components/editor-ask-selection-button';
import { EditorBubble } from './components/editor-bubble';
import { EditorCommand, EditorCommandList } from './components/editor-command';
import {
  EditorCommandEmpty,
  EditorCommandItem,
} from './components/editor-command-item';
import { EditorSearchBar } from './components/editor-search-bar';
import { EditorSearchFocus } from './components/editor-search-focus';
import { EditorTableControls } from './components/editor-table-controls';
import {
  defaultExtensions,
  defaultSuggestionItems,
  handleCommandNavigation,
  type ImageUploadBucket,
} from './extensions/';
import { ColorSelector } from './selectors/color';
import { LinkSelector } from './selectors/link';
import { EMAIL_HIDDEN_NODES, NodeSelector } from './selectors/node';
import { TextButtons } from './selectors/text-buttons';
import { isFindShortcut } from './utils/keyboard';

import type { EditorEvents, Extensions } from '@tiptap/core';

import './editor.css';

/**
 * Named editor feature sets. `full` is the default; `email` is a restricted set
 * with fewer formatting controls.
 */
export type EditorFeatureSet = 'full' | 'email';

/** Contenteditable typography density. `base` is the default; `sm` is for compact wells. */
export type EditorProseSize = 'base' | 'sm';

/** True for the underline (Mod+U) and highlight (Mod+Shift+H) shortcuts. */
function isEmailBlockedFormatShortcut(event: KeyboardEvent): boolean {
  if (event.altKey || !(event.metaKey || event.ctrlKey)) return false;
  const key = event.key.toLowerCase();
  return key === 'u' || (event.shiftKey && key === 'h');
}

export interface BaseEditorProps<T> {
  id: string;
  autofocus?: boolean;
  /**
   * Whether the editor is editable.
   * When false, the editor becomes read-only (selection/copy still works).
   * @default true
   */
  editable?: boolean;
  additionalExtensions?: Extensions;
  initialContent?: T;
  dir?: 'ltr' | 'rtl';
  onCreate?: (props: EditorEvents['create']) => void;
  onUpdate: (props: EditorEvents['update']) => void;
  onBlur?: (props: EditorEvents['blur']) => void;
  ariaLabel?: string;
  /**
   * Whether to automatically find the nearest scrollable parent element.
   * When true, searches up the DOM tree for a scrollable container.
   * When false, uses document.body as the container.
   * @default true
   */
  findScrollableParent?: boolean;
  /**
   * Configure where image uploads are stored.
   */
  imageUploadBucket?: ImageUploadBucket;
  className?: string;
  /** Extension names to exclude from the default set */
  excludeExtensions?: string[];
  extensionMode?: 'full' | 'readonly';
  /**
   * Adds vertical space after the editor so floating UIs
   * (bubble menu, slash commands, link popover) aren't clipped.
   * @default false
   */
  bottomClearance?: boolean;
  /** Placeholder text for empty nodes */
  placeholder?: string;
  placeholderOnlyWhenEmpty?: boolean;
  /** Called when the user asks Littlebird about the current editor selection */
  onAskSelection?: (selection: { text: string }) => void;
  /** Which editor feature set to expose. @default 'full' */
  featureSet?: EditorFeatureSet;
  /** Contenteditable typography density. @default 'base' */
  proseSize?: EditorProseSize;
  /** Optional container for the editor's find and replace controls. */
  findPortalContainer?: HTMLElement | null;
  children?: ReactNode;
}

function BaseEditorContent<T>({
  id,
  autofocus = true,
  editable = true,
  initialContent,
  additionalExtensions = [],
  dir = 'ltr',
  onCreate,
  onUpdate,
  imageUploadBucket,
  className,
  excludeExtensions,
  extensionMode = 'full',
  bottomClearance = false,
  placeholder,
  placeholderOnlyWhenEmpty,
  onBlur,
  ariaLabel,
  onAskSelection,
  featureSet = 'full',
  proseSize = 'base',
  findPortalContainer,
  children,
}: BaseEditorProps<T>) {
  const isReadonly = extensionMode === 'readonly';
  // Ref so the captured-once keydown handler reads the current feature set.
  const featureSetRef = useRef(featureSet);
  featureSetRef.current = featureSet;

  const rootRef = useRef<HTMLDivElement>(null);
  const extensionsRef = useRef([
    ...defaultExtensions({
      editorId: id,
      mode: extensionMode,
      exclude: excludeExtensions,
      placeholder:
        placeholder || placeholderOnlyWhenEmpty
          ? { text: placeholder, onlyWhenEmpty: placeholderOnlyWhenEmpty }
          : undefined,
      imageUploadBucket,
    }),
    ...additionalExtensions,
  ]);
  const [openNodeSelector, setOpenNodeSelector] = useState(false);
  const [openLinkSelector, setOpenLinkSelector] = useState(false);
  const [openColorSelector, setOpenColorSelector] = useState(false);

  const editorContainer = useEditorContainer();
  const { searchFocus } = useSearchFocusState({ editorId: id });
  const skipInitialScrollRestore = Boolean(searchFocus.focusQuery);

  const suggestionItems =
    featureSet === 'email'
      ? defaultSuggestionItems.filter(
          (item) => !EMAIL_HIDDEN_NODES.has(item.title)
        )
      : defaultSuggestionItems;

  return (
    <div
      ref={rootRef}
      className={cn('lb-editor lb-editor-table-root smoothdown p-6', className)}
      dir={dir}
      data-deny-selection
    >
      <EditorRoot>
        <EditorContent
          autofocus={autofocus}
          editable={editable}
          editorProps={{
            handleDOMEvents: {
              keydown: (_view, event) => {
                // Keep Cmd/Ctrl+F scoped to this editor so it doesn't bubble to
                // ancestor find handlers (e.g. useVirtualListFind) and open a second bar.
                if (!isReadonly && isFindShortcut(event)) {
                  event.stopPropagation();
                }
                if (
                  featureSetRef.current === 'email' &&
                  isEmailBlockedFormatShortcut(event)
                ) {
                  event.preventDefault();
                  return true;
                }
                return handleCommandNavigation(event);
              },
            },
            attributes: {
              class: classList(
                'markdown',
                'prose prose-bird select-text cursor-text',
                'focus:outline-none',
                proseSize === 'sm' ? 'prose-sm' : 'prose-base'
              ),
              ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            },
          }}
          initialContent={initialContent as never}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onBlur={onBlur}
          extensions={extensionsRef.current}
          immediatelyRender={false}
        >
          {children}
          <EditorTableControls
            editable={editable && !isReadonly}
            rootRef={rootRef}
          />
          <EditorSearchFocus editorId={id} />
          {!isReadonly && (
            <>
              <EditorSearchBar portalContainer={findPortalContainer} />
              <EditorBubble>
                {onAskSelection && (
                  <EditorAskSelectionButton onAskSelection={onAskSelection} />
                )}
                <NodeSelector
                  open={openNodeSelector}
                  onOpenChange={setOpenNodeSelector}
                  featureSet={featureSet}
                />
                <LinkSelector
                  open={openLinkSelector}
                  onOpenChange={setOpenLinkSelector}
                />
                <TextButtons featureSet={featureSet} />
                {featureSet !== 'email' && (
                  <ColorSelector
                    open={openColorSelector}
                    onOpenChange={setOpenColorSelector}
                  />
                )}
              </EditorBubble>
            </>
          )}
          {!isReadonly && (
            <EditorCommand
              className={classList(
                'editor-command',
                'z-50',
                'h-auto max-h-[330px] w-72',
                'overflow-y-auto',
                'rounded-xl smoothing-default',
                'border border-border-strong',
                'bg-surface',
                'shadow-md transition-all',
                'motion-scale-in-95 motion-opacity-in-0 motion-duration-200',
                'px-2'
              )}
              style={{
                transformOrigin: 'var(--radix-popper-transform-origin)',
              }}
            >
              <EditorCommandEmpty className="px-0 py-2 text-foreground-muted text-xs">
                <XStack className="w-full gap-2 items-center justify-between">
                  Type '/' on the page <HotkeyLabel keyName="esc" />
                </XStack>
              </EditorCommandEmpty>
              <EditorCommandList>
                {suggestionItems.map((item) => (
                  <EditorCommandItem
                    value={item.title}
                    onCommand={(val) => item?.command?.(val)}
                    className={classList(
                      'flex items-center',
                      'w-full space-x-2',
                      'rounded-lg smoothing-default',
                      'p-2',
                      'text-left text-sm',
                      'hover:bg-surface-active',
                      'aria-selected:bg-surface-active',
                      'mt-2 last:mb-2',
                      'scroll-my-2'
                    )}
                    key={item.title}
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg smoothing-default border border-border-strongest">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-foreground-subtle">{item.title}</p>
                      <p className="text-xs text-foreground-muted">
                        {item.description}
                      </p>
                    </div>
                  </EditorCommandItem>
                ))}
              </EditorCommandList>
            </EditorCommand>
          )}
        </EditorContent>
      </EditorRoot>
      {bottomClearance && <div className="h-[25vh]" />}
      <ScrollRestoration
        id={`editor-scroll-restoration-${id}`}
        skipInitialRestore={skipInitialScrollRestore}
        onRestoreScroll={(offset) =>
          requestAnimationFrame(() =>
            editorContainer?.scrollTo({ top: offset })
          )
        }
      />
    </div>
  );
}

export function BaseEditor<T>({
  findScrollableParent = true,
  id,
  ...rest
}: BaseEditorProps<T>) {
  return (
    <BaseEditorProvider
      editorId={id}
      findScrollableParent={findScrollableParent}
    >
      <BaseEditorContent id={id} {...rest} />
    </BaseEditorProvider>
  );
}
